import { useCallback, useEffect } from "react"

import { Box, Card, CircularProgress, Typography } from "@mui/material"
import { Stack } from "@mui/system"
import { m, AnimatePresence } from "framer-motion"

import { useTranslate } from "src/locales"
import { useGetBackgroundTasksQuery } from "src/services/system/system.service"
import { BackgroundTaskStatus, BackgroundTaskType } from "src/services/system/system.types"
import { updateBackgroundTaskState } from "src/store/slices/app/appSlice"
import { useAppDispatch, useAppSelector } from "src/store/store"

export default function BackgroundTaskManager() {

  const { t } = useTranslate()

  const backgroundState = useAppSelector(state => state.app.background_task_state);
  const dispatch = useAppDispatch();

  const { data: backgroundTasks } = useGetBackgroundTasksQuery({
    status: [BackgroundTaskStatus.PENDING, BackgroundTaskStatus.IN_PROGRESS]
  }, {
    pollingInterval: backgroundState.pending_count > 0 ? 3000 : undefined
  });

  useEffect(() => {
    if (backgroundTasks) {
      const inProgressTask = backgroundTasks.find(task => [BackgroundTaskStatus.IN_PROGRESS].includes(task.status));
      const pendingTasks = backgroundTasks.filter(task => [BackgroundTaskStatus.PENDING, BackgroundTaskStatus.IN_PROGRESS].includes(task.status));
      const completedTasks = backgroundTasks.filter(task => task.status === BackgroundTaskStatus.COMPLETED);
      const failedTasks = backgroundTasks.filter(task => task.status === BackgroundTaskStatus.FAILED);

      dispatch(updateBackgroundTaskState({
        task_type: inProgressTask?.type || '' as BackgroundTaskType,
        total_count: backgroundTasks.length,
        pending_count: pendingTasks.length,
        completed_count: completedTasks.length,
        failed_count: failedTasks.length
      }))
    }
  }, [backgroundTasks, dispatch]);

  const getTaskText = useCallback(() => {

    if (!backgroundState.pending_count) return null;

    switch (backgroundState.task_type) {
      case BackgroundTaskType.FINALIZE_APPLY_INTENT:
        return t('system.background_tasks.info_labels.finalize_apply_intent', { completed: backgroundState.completed_count, total: backgroundState.total_count });
      case BackgroundTaskType.FINALIZE_CONNECT_INTENT:
        return t('system.background_tasks.info_labels.finalize_connect_intent', { completed: backgroundState.completed_count, total: backgroundState.total_count });
      case BackgroundTaskType.IMPORT_CANDIDATE_PROFILE:
        return t('system.background_tasks.info_labels.import_candidate_profile');
      case BackgroundTaskType.IMPORT_APPLICATION_PROFILE:
        return t('system.background_tasks.info_labels.import_application_profile');
      default:
        return null;
    }

  }, [t, backgroundState]);

  if (!backgroundState.pending_count) return null;

  return (
    <AnimatePresence>
      <m.div
        initial={{ y: -100, opacity: 0, translateY: '50%' }}
        animate={{ y: 12, opacity: 1, translateY: 0 }}
        exit={{ y: -100, opacity: 0 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
        style={{
          position: 'fixed',
          top: 5,
          left: '50%',
          width: '50%',
          transform: 'translate(-50%, 0)',
          marginLeft: '-25%',
          zIndex: 1200,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Card
          sx={{
            boxShadow: (theme) => theme.customShadows.z16,
            borderRadius: 2,
            p: 2,
            bgcolor: (theme) => theme.palette.info.darker,
            maxWidth: 320,
            width: 'auto',
          }}
        >
          <Stack direction='row' spacing={2} alignItems='center' justifyContent="space-between" sx={{ overflow: 'hidden' }}>
            <Box>
              <Typography variant='subtitle2' color="white" textAlign="center">
                {getTaskText()}
              </Typography>
            </Box>
            <Box>
              <CircularProgress size={24} color='info' />
            </Box>
          </Stack>
        </Card>
      </m.div>
    </AnimatePresence>
  )
}